import { useState, useEffect } from "react";
import { Header } from "../common/Header";
import { TermsCheckbox } from "./TermsCheckbox";
import { SignInButton } from "./SignInButton";
import { useNavigate, useSearchParams } from "react-router";
import { MobileInput } from "./MobileInput";
import { generateOtp, isAuthenticated } from "@nusrif/services";
import { clearHistoryAndNavigate } from "@nusrif/utils";
import config from "@nusrif/config";
export const SignInPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState("");
  const [inputType, setInputType] = useState<'email' | 'mobile' | 'unknown'>('mobile');
  const [isValid, setIsValid] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Get return URL from search params
  const returnUrl = searchParams.get('returnUrl') || '/';
  // Check if user is already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      // Clear history and navigate to prevent back navigation to signin
      clearHistoryAndNavigate(navigate, returnUrl);
    }
  }, [navigate, returnUrl]);
  // Detect input type and validate
  useEffect(() => {
    if (!inputValue.trim()) {
      setInputType('unknown');
      setIsValid(false);
      return;
    }
    // Check if input contains @ symbol (email)
    if (inputValue.includes('@')) {
      setInputType('email');
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setIsValid(emailRegex.test(inputValue));
    } else {
      // Check if input contains only numbers, spaces, +, -, () (mobile)
      const mobileRegex = /^[\d\s\+\-\(\)]+$/;
      if (mobileRegex.test(inputValue)) {
        setInputType('mobile');
        // Mobile validation (10-15 digits)
        const digitsOnly = inputValue.replace(/\D/g, '');
        setIsValid(digitsOnly.length >= 10 && digitsOnly.length <= 15);
      } else {
        setInputType('unknown');
        setIsValid(false);
      }
    }
  }, [inputValue]);
  const handleSignIn = async () => {
    if (!isValid) return;
    setIsLoading(true);
    try {
      let phoneNumber = inputValue;
      // Append country code to the input value if it doesn't exist and remove all whitespaces
      phoneNumber = phoneNumber.replace(/\s/g, '');
      if (!phoneNumber.startsWith(config.region.mobileCountryCode)) {
        phoneNumber = config.region.mobileCountryCode + phoneNumber;
      }
      
      const response = await generateOtp({
        phoneNumber,
        otpType: 'LOGIN',
        deliveryMethod: 'SMS',
      });
      
      // Navigate to OTP verification page with contact info and return URL
      const paramsObj = {
        contact: phoneNumber,
        type: inputType,
        returnUrl: returnUrl,
        ...(response.data && response.data.code ? { code: response.data.code } : {})
      };
      
      const params = new URLSearchParams(paramsObj);
      navigate(`/otp-verification?${params.toString()}`);
    } catch (error) {
      console.error('Failed to send OTP:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const canSignIn = isValid && !isLoading;
  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* Header */}
      <Header 
        title="Sign In" 
        showBackButton={true} 
        showCartButton={false}
      />
      
      {/* Main Content */}
      <div className="pt-20 px-5">
        <div className="max-w-md mx-auto space-y-8">
          {/* Welcome Section */}
          <div className="text-center py-8">
            <h1 className="text-3xl font-bold text-black mb-4">Welcome to Two And Five!</h1>
            <p className="text-gray-600 text-lg">
              Sign in to your account to continue shopping with us
            </p>
          </div>
          {/* Sign In Form */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-6">
            {/* Adaptive Input */}
            <MobileInput
              value={inputValue}
              onChange={setInputValue}
              isValid={isValid}
            />
            {/* Terms & Conditions */}
            <TermsCheckbox
              checked={termsAccepted}
              onChange={setTermsAccepted}
            />
            {/* Sign In Button */}
            <SignInButton
              onClick={handleSignIn}
              disabled={!canSignIn}
              loading={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};